import { CommonModule } from '@angular/common'
import { NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { AwsAccountAccessViewComponent } from './account-access-view/account-access-view.component'
import { AppResourcesSelectorComponent } from './app-resources-selector/app-resources-selector.component'
import { AppResourceRuleSelectorComponent } from './app-resource-rule-selector/app-resource-rule-selector.component'
import { ArpioCenterPanelComponent } from './arpio-center-panel/arpio-center-panel.component'
import { CopyClipboardDirective } from './clipboard-copy'
import { FocusElementDirective } from './focus-element'
import { ModalDialogComponent } from './modal-dialog/modal-dialog.component'
import { RpRangeSliderComponent } from './rp-range-slider/rp-range-slider.component'
import { RpoRangeSliderComponent } from './rpo-range-slider/rpo-range-slider.component'
import { BusyButtonDirective } from './busy-button.directive'
import { RecoveryTimeInfoComponent } from './recovery-time-info/recovery-time-info.component'
import { AboutRealTimeReplicationComponent } from './about-real-time-replication/about-real-time-replication.component'
import { FilterFormComponent } from './filter-form/filter-form.component'
import { StringListControlComponent } from './string-list-control/string-list-control.component'
import { LucideIconsModule } from './lucide-icons.module'

// ngPrime component imports
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { AccordionModule } from 'primeng/accordion'
import { TooltipModule } from 'primeng/tooltip'
import { ToastModule } from 'primeng/toast'
import { TableModule } from 'primeng/table'
import { ButtonModule } from 'primeng/button'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { SliderModule } from 'primeng/slider'
import { SelectModule } from 'primeng/select'
import { TagModule } from 'primeng/tag' // useful
import { MultiSelectModule } from 'primeng/multiselect' // useful
import { ChipModule } from 'primeng/chip'
import { BadgeModule } from 'primeng/badge'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { ArpioAccountsListComponent } from '../arpio/components/arpio-root/components/accounts-dashboard-table/arpio-accounts-list.component'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { TextareaModule } from 'primeng/textarea'
import { FloatLabelModule } from 'primeng/floatlabel'
import { SafeUrlPipe } from './pipes/safe-url/safe-url.pipe'
import { DatePickerModule } from 'primeng/datepicker'
import { TruncatedListComponent } from './truncated-list/truncated-list.component'
import { ScrollingModule } from '@angular/cdk/scrolling'
import { TreeModule } from 'primeng/tree'
import { TreeNodeComponent } from './tree-node/tree-node.component'
import { AppResourceSelectionComponent } from './app-resource-selection/app-resource-selection.component'
import { SkeletonModule } from 'primeng/skeleton'
import { DragDropModule } from '@angular/cdk/drag-drop'
import { RegionDropdownComponent } from './region-dropdown/region-dropdown.component'
import { AzureAccessViewComponent } from './azure-access-view/azure-access-view.component'
import { CustomRuleDropdownComponent } from './custom-rule-dropdown/custom-rule-dropdown.component'
import { TabsComponent } from './components/tabs/tabs.component'
import { BannerComponent } from './components/banner/banner.component'
import { ErrorBlockComponent } from './components/error-block/error-block.component'
import { FieldIssueComponent } from './components/field-issue/field-issue.component'
import { FieldErrorHostDirective } from './directives/field-error-host.directive'
import { ObjectErrorHostDirective } from './directives/object-error-host.directive'
import { SurfaceErrorHostDirective } from './directives/surface-error-host.directive'
import { StickyHeightDirective } from './directives/sticky-height.directive'
import { TooltipViewportFitDirective } from './directives/tooltip-viewport-fit.directive'
import { StacksetsInstructionsComponent } from './stacksets-instructions/stacksets-instructions.component'
import { StacksetsUpdateInstructionsComponent } from './stacksets-update-instructions/stacksets-update-instructions.component'

@NgModule({
  declarations: [
    AwsAccountAccessViewComponent,
    AzureAccessViewComponent,
    AppResourceRuleSelectorComponent,
    AppResourcesSelectorComponent,
    RpoRangeSliderComponent,
    FocusElementDirective,
    ModalDialogComponent,
    ArpioCenterPanelComponent,
    RpRangeSliderComponent,
    BusyButtonDirective,
    RecoveryTimeInfoComponent,
    AboutRealTimeReplicationComponent,
    FilterFormComponent,
    StringListControlComponent,
    ArpioAccountsListComponent,
    SafeUrlPipe,
    TruncatedListComponent,
    TreeNodeComponent,
    AppResourceSelectionComponent,
    RegionDropdownComponent,
    CustomRuleDropdownComponent,
    TabsComponent,
    BannerComponent,
    ErrorBlockComponent,
    FieldIssueComponent,
    FieldErrorHostDirective,
    SurfaceErrorHostDirective,
    ObjectErrorHostDirective,
    StickyHeightDirective,
    TooltipViewportFitDirective,
    BannerComponent,
    StacksetsInstructionsComponent,
    StacksetsUpdateInstructionsComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    RouterModule,
    LucideIconsModule,

    // ngPrime Components: https://www.primefaces.org/primeng-v16-lts/accordion
    AccordionModule,
    TooltipModule,
    TableModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SliderModule,
    SelectModule, // important
    TagModule, // important
    MultiSelectModule,
    ChipModule,
    BadgeModule,
    ScrollPanelModule,
    ToggleSwitchModule,
    TextareaModule,
    FloatLabelModule,
    DatePickerModule,
    ScrollingModule,
    TreeModule,
    ScrollingModule,
    SkeletonModule,
    CopyClipboardDirective,
  ],
  exports: [
    BrowserAnimationsModule,
    LucideIconsModule,
    AwsAccountAccessViewComponent,
    AzureAccessViewComponent,
    AppResourceRuleSelectorComponent,
    AppResourcesSelectorComponent,
    RpoRangeSliderComponent,
    RpRangeSliderComponent,
    CopyClipboardDirective,
    BusyButtonDirective,
    FocusElementDirective,
    ModalDialogComponent,
    ArpioCenterPanelComponent,
    RecoveryTimeInfoComponent,
    AboutRealTimeReplicationComponent,
    FilterFormComponent,
    StringListControlComponent,
    AccordionModule,
    ArpioAccountsListComponent,
    SafeUrlPipe,
    TruncatedListComponent,
    TreeNodeComponent,
    AppResourceSelectionComponent,
    RegionDropdownComponent,
    CustomRuleDropdownComponent,
    TabsComponent,
    BannerComponent,
    ErrorBlockComponent,
    FieldIssueComponent,
    FieldErrorHostDirective,
    SurfaceErrorHostDirective,
    ObjectErrorHostDirective,
    StickyHeightDirective,
    TooltipViewportFitDirective,
    BannerComponent,
    StacksetsInstructionsComponent,
    StacksetsUpdateInstructionsComponent,

    // ngPrime Components:
    TooltipModule,
    ToastModule,
    TableModule,
    SliderModule, // needed for range
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule, // Style this to be same as Arpio
    SelectModule, // important
    TagModule, // important
    MultiSelectModule,
    ChipModule,
    BadgeModule,
    ScrollPanelModule,
    ToggleSwitchModule,
    TextareaModule,
    FloatLabelModule,
    DatePickerModule,
    ScrollingModule,
    TreeModule,
    ScrollingModule,
    SkeletonModule,
  ],
})
export class ArpioSharedModule {}
